import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ReceiptText, RefreshCw, AlertCircle, CheckCircle2, XCircle,
  Building2, Loader2, FileText, Hash, Play, UserX, Search, Percent, Eye, X, Download, Trash2,
  AlertTriangle, Pencil, Check, ArrowLeftRight, ChevronDown, ChevronRight, ListFilter
} from 'lucide-react';
import { toast } from 'sonner';
import useDraggableModal from '../hooks/useDraggableModal';

const fmtMoney = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const s = String(iso);
  // Fecha "solo-fecha" (YYYY-MM-DD): formatear sin pasar por Date() para evitar
  // el corrimiento de día por zona horaria (new Date("2026-06-12") = UTC → -3h).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try {
    return new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return s; }
};

// clave única por retención dentro de un documento
const keyOf = (docId, idx) => `${docId}#${idx}`;

const RetencionesClientesPage = () => {
  const { token, isAdmin, canDeleteIn } = useAuth();
  // Descartar documentos es la acción de ELIMINAR de esta herramienta: la habilita
  // el check "permite eliminar" de Administrar Usuarios, no el rol. `isAdmin` se
  // sigue usando para las correcciones (tipo / clase de documento), que son otra
  // cosa y tienen su propio gate en el backend.
  const puedeDescartar = canDeleteIn('cobranzas');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [pendientes, setPendientes] = useState([]);
  const [cargandoPend, setCargandoPend] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [analizando, setAnalizando] = useState(false);
  const [documentos, setDocumentos] = useState([]);   // resultado del análisis
  const [errores, setErrores] = useState([]);
  const [error, setError] = useState(null);

  // Catálogo de tipos y edición inline del tipo de un documento (corrección admin).
  const [tiposRetencion, setTiposRetencion] = useState([]);
  const [editandoTipo, setEditandoTipo] = useState(null);   // load_doc_id en edición
  const [tipoEditValor, setTipoEditValor] = useState('');   // id seleccionado en el editor
  const [guardandoTipo, setGuardandoTipo] = useState(false);

  // clie_id elegido por retención (key -> id)
  const [seleccionCliente, setSeleccionCliente] = useState({});
  // fact_id elegido por retención (key -> id de factura a imputar)
  const [seleccionFactura, setSeleccionFactura] = useState({});
  // estado por retención (key -> 'idle'|'loading'|'ok'|'error')
  const [estados, setEstados] = useState({});
  // resultado por retención (key -> { cobro_id, error })
  const [resultados, setResultados] = useState({});
  // búsqueda manual por retención (key -> [candidatos])
  const [busquedaManual, setBusquedaManual] = useState({});
  // k -> bool: se están trayendo las facturas del cliente recién elegido.
  const [cargandoFacturas, setCargandoFacturas] = useState({});

  // ---- Órdenes de pago (flujo OP) ----
  const [ordenes, setOrdenes] = useState([]);          // resultado de /analizar-op
  const [opTipos, setOpTipos] = useState({});          // opId -> { idxRet: tipoId }
  const [opCerts, setOpCerts] = useState({});          // opId -> { idxRet: numero_certificado }
  const [opCliente, setOpCliente] = useState({});      // opId -> clie_id_clie elegido
  const [opEmitir, setOpEmitir] = useState({});        // opId -> recibo pendiente a emitir ('' = ninguno)
  const [opEstados, setOpEstados] = useState({});      // opId -> 'idle'|'loading'|'ok'|'conciliado'|'error'
  const [opResultados, setOpResultados] = useState({});
  const [opBusqueda, setOpBusqueda] = useState({});     // opId -> resultados de la búsqueda manual de cliente

  // ---- Layout del análisis ----
  // La tabla de pendientes se pliega al terminar un análisis: los resultados
  // nacían abajo de una tabla larga y quedaban fuera de pantalla. Plegarla los
  // sube al viewport y saca de en medio una lista que en ese momento no se usa.
  const [listaAbierta, setListaAbierta] = useState(true);
  const [analizadosIds, setAnalizadosIds] = useState(() => new Set());  // filas "en análisis"
  const [destacar, setDestacar] = useState(false);      // ring de entrada en los resultados
  const resultadosRef = useRef(null);
  const listaRef = useRef(null);

  // Visor de PDF en modal (sirve el archivo del filesystem; el admin puede verlo).
  const [visor, setVisor] = useState({ open: false, url: '', nombre: '', cargando: false });
  const dragVisor = useDraggableModal(visor.open);

  const abrirVisor = useCallback(async (docId, nombre) => {
    if (!docId) { toast.error('No hay archivo asociado a este documento.'); return; }
    setVisor({ open: true, url: '', nombre: nombre || 'Documento', cargando: true });
    try {
      const res = await fetch(`${API_URL}/api/load-docs/file/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setVisor((v) => ({ ...v, url, cargando: false }));
    } catch (err) {
      toast.error('No se pudo abrir el PDF: ' + err.message);
      setVisor({ open: false, url: '', nombre: '', cargando: false });
    }
  }, [API_URL, token]);

  const cerrarVisor = useCallback(() => {
    setVisor((v) => {
      if (v.url) URL.revokeObjectURL(v.url);
      return { open: false, url: '', nombre: '', cargando: false };
    });
  }, []);

  useEffect(() => {
    if (!visor.open) return;
    const onKey = (e) => { if (e.key === 'Escape') cerrarVisor(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visor.open, cerrarVisor]);

  useEffect(() => () => { if (visor.url) URL.revokeObjectURL(visor.url); }, [visor.url]);

  const cargarPendientes = useCallback(async () => {
    setCargandoPend(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/pendientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setPendientes(data.documentos || []);
    } catch (err) {
      toast.error('Error al cargar pendientes: ' + err.message);
    } finally {
      setCargandoPend(false);
    }
  }, [API_URL, token]);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  const cargarTipos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/tipos-retencion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTiposRetencion(data.tipos || []);
      }
    } catch {
      /* silencioso: la corrección queda deshabilitada si no carga el catálogo */
    }
  }, [API_URL, token]);

  useEffect(() => { cargarTipos(); }, [cargarTipos]);

  const abrirEdicionTipo = (d) => {
    setEditandoTipo(d.id);
    setTipoEditValor(d.tipo_retencion_id != null ? String(d.tipo_retencion_id) : '');
  };

  const cancelarEdicionTipo = () => {
    setEditandoTipo(null);
    setTipoEditValor('');
  };

  const guardarTipo = async (docId) => {
    if (!tipoEditValor) { toast.error('Elegí un tipo de retención.'); return; }
    setGuardandoTipo(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/corregir-tipo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ load_doc_id: docId, tipo_retencion_id: Number(tipoEditValor) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      // Actualiza la fila en memoria sin recargar todo.
      setPendientes((prev) => prev.map((d) =>
        d.id === docId
          ? { ...d, tipo_retencion_id: data.tipo_retencion_id, tipo_retencion_nombre: data.tipo_retencion_nombre, tipo_alerta: data.tipo_alerta }
          : d
      ));
      toast.success(`Tipo corregido a ${data.tipo_retencion_nombre}.`);
      cancelarEdicionTipo();
    } catch (err) {
      toast.error('No se pudo corregir el tipo: ' + err.message);
    } finally {
      setGuardandoTipo(false);
    }
  };

  // La clase (retención / orden de pago) la asigna el detector al subir.
  // Si se equivocó, el admin la corrige acá.
  const corregirDocumento = async (d) => {
    const nuevo = d.tipo_documento === 'orden_pago' ? 'retencion' : 'orden_pago';
    const label = nuevo === 'orden_pago' ? 'Orden de pago' : 'Retención';
    if (!window.confirm(`¿Cambiar la clase de "${d.archivo_nombre}" a ${label}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/corregir-documento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ load_doc_id: d.id, tipo_documento: nuevo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}`);
      setPendientes((prev) => prev.map((x) =>
        x.id === d.id
          ? {
              ...x,
              tipo_documento: data.tipo_documento,
              ...(data.tipo_documento === 'orden_pago' ? { tipo_retencion_id: null, tipo_retencion_nombre: '' } : {}),
            }
          : x
      ));
      toast.success(`Clase cambiada a ${label}.`);
    } catch (err) {
      toast.error('No se pudo cambiar la clase: ' + err.message);
    }
  };

  const toggleSel = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleTodos = () => {
    setSeleccionados((prev) =>
      prev.size === pendientes.length ? new Set() : new Set(pendientes.map((d) => d.id))
    );
  };

  // Descarta documentos pendientes (solo admin) para limpiar el listado.
  const descartar = async (ids) => {
    const lista = (ids || []).filter(Boolean);
    if (lista.length === 0) { toast.info('Seleccioná al menos un documento.'); return; }
    const msg = lista.length === 1
      ? '¿Descartar este documento pendiente? Saldrá del listado (no se borra la carga del usuario).'
      : `¿Descartar ${lista.length} documentos pendientes? Saldrán del listado (no se borran las cargas de los usuarios).`;
    if (!window.confirm(msg)) return;

    setDescartando(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/descartar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_doc_ids: lista }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setSeleccionados((prev) => {
        const next = new Set(prev);
        lista.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`${data.descartados || 0} documento(s) descartado(s).`);
      cargarPendientes();
    } catch (err) {
      toast.error('Error al descartar: ' + err.message);
    } finally {
      setDescartando(false);
    }
  };

  const resetAnalisis = () => {
    setDocumentos([]); setErrores([]); setError(null);
    setSeleccionCliente({}); setSeleccionFactura({}); setEstados({}); setResultados({}); setBusquedaManual({});
    setOrdenes([]); setOpTipos({}); setOpCerts({}); setOpCliente({}); setOpEmitir({}); setOpEstados({}); setOpResultados({});
    setOpBusqueda({}); setAnalizadosIds(new Set());
  };

  // Cierra el análisis en curso y devuelve al usuario a la lista completa.
  const volverALista = ({ limpiar = false } = {}) => {
    if (limpiar) resetAnalisis();
    setListaAbierta(true);
    requestAnimationFrame(() => {
      listaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const analizar = useCallback(async () => {
    if (seleccionados.size === 0) { toast.info('Seleccioná al menos un documento.'); return; }
    setAnalizando(true);
    resetAnalisis();

    // Separar por clase de documento: retenciones sueltas vs órdenes de pago.
    const ids = Array.from(seleccionados);
    const idsOp = ids.filter((id) => pendientes.find((p) => p.id === id)?.tipo_documento === 'orden_pago');
    const idsRet = ids.filter((id) => !idsOp.includes(id));

    const erroresAcum = [];
    try {
      // --- Retenciones sueltas (flujo actual) ---
      if (idsRet.length > 0) {
        const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/analizar`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ load_doc_ids: idsRet }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);

        const docs = data.documentos || [];
        setDocumentos(docs);
        erroresAcum.push(...(data.errores || []));

        const sel = {}; const fac = {}; const est = {};
        docs.forEach((doc) => {
          (doc.retenciones || []).forEach((r, idx) => {
            const k = keyOf(doc.load_doc_id, idx);
            if (r.clie_id_clie) sel[k] = r.clie_id_clie;
            if (r.factura_id) fac[k] = r.factura_id;   // auto-match del PDF
            est[k] = 'idle';
          });
        });
        setSeleccionCliente(sel);
        setSeleccionFactura(fac);
        setEstados(est);
      }

      // --- Órdenes de pago ---
      if (idsOp.length > 0) {
        const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/analizar-op`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ load_doc_ids: idsOp }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);

        const ops = data.ordenes || [];
        setOrdenes(ops);
        erroresAcum.push(...(data.errores || []));

        const tipos = {}; const clientes = {}; const emitir = {}; const est = {}; const certs = {};
        ops.forEach((op) => {
          const id = op.load_doc_id;
          est[id] = 'idle';
          tipos[id] = {};
          certs[id] = {};
          (op.orden_pago?.retenciones || []).forEach((r, idx) => {
            if (r.tipo_sugerido_id) tipos[id][idx] = r.tipo_sugerido_id;
            certs[id][idx] = r.numero_certificado || '';
          });
          if (op.cliente?.clie_id_clie) clientes[id] = op.cliente.clie_id_clie;
          // Pre-seleccionar el recibo de medios Pendiente si es el único candidato.
          const pend = (op.conciliacion?.medios?.candidatos || []).filter((c) => c.esta_codigo === 10);
          if (op.conciliacion?.medios?.estado === 'pendiente' && pend.length === 1) {
            emitir[id] = pend[0].id;
          }
        });
        setOpTipos(tipos);
        setOpCerts(certs);
        setOpCliente(clientes);
        setOpEmitir(emitir);
        setOpEstados(est);
      }

      setErrores(erroresAcum);
      setAnalizadosIds(new Set(ids));
      // Se pliega la lista y se baja al resultado sólo si hay algo que trabajar.
      // Si el análisis no devolvió nada (todo error), la lista queda abierta
      // porque el usuario tiene que volver a elegir documentos.
      const hubo = (idsRet.length + idsOp.length) > 0 && erroresAcum.length < ids.length;
      if (hubo) setListaAbierta(false);
      toast.success(`${idsRet.length + idsOp.length} documento(s) analizado(s)${idsOp.length ? ` (${idsOp.length} OP)` : ''}.`);
      if (erroresAcum.length) toast.warning(`${erroresAcum.length} documento(s) con error.`);
    } catch (err) {
      setError(err.message);
      toast.error('Error al analizar: ' + err.message);
    } finally {
      setAnalizando(false);
    }
  }, [API_URL, token, seleccionados, pendientes]);

  // Baja al primer resultado y lo destaca un instante. Va en un efecto (no en
  // `analizar`) para que corra DESPUÉS de que React pintó las tarjetas: si no,
  // el nodo todavía no existe y el scroll no tiene a dónde ir.
  const hayResultados = documentos.length > 0 || ordenes.length > 0;
  useEffect(() => {
    if (!hayResultados) return;
    const t1 = requestAnimationFrame(() => {
      resultadosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    setDestacar(true);
    const t2 = setTimeout(() => setDestacar(false), 2000);
    return () => { cancelAnimationFrame(t1); clearTimeout(t2); };
  }, [hayResultados]);

  const buscarManual = async (k, q) => {
    if (!q || q.trim().length < 2) { toast.info('Ingresá CUIT o nombre (mín. 2).'); return; }
    try {
      const res = await fetch(
        `${API_URL}/api/admin/retenciones-clientes/buscar-cliente?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setBusquedaManual((p) => ({ ...p, [k]: data.resultados || [] }));
      if (!(data.resultados || []).length) toast.info('Sin resultados.');
    } catch (err) {
      toast.error('Error en la búsqueda: ' + err.message);
    }
  };

  // Trae las facturas pendientes del cliente elegido y las carga en esa
  // retención. Hace falta porque el smartie de facturas se consulta por ID de
  // cliente (no por CUIT): hasta no saber qué cliente es, no se pueden pedir.
  const cargarFacturasCliente = async (loadDocId, idx, clieId, montoRetenido) => {
    const k = keyOf(loadDocId, idx);
    if (!clieId) return;
    setCargandoFacturas((p) => ({ ...p, [k]: true }));
    try {
      const res = await fetch(
        `${API_URL}/api/admin/retenciones-clientes/facturas-cliente?clie_id=${clieId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const facturas = data.facturas || [];

      setDocumentos((docs) => docs.map((d) => {
        if (d.load_doc_id !== loadDocId) return d;
        return {
          ...d,
          retenciones: (d.retenciones || []).map((r, i) => (
            i !== idx ? r : {
              ...r,
              facturas,
              estado_factura: facturas.length ? 'seleccion' : 'sin_facturas',
            }
          )),
        };
      }));

      // Si el monto retenido coincide con el pendiente de una sola factura,
      // la preseleccionamos (mismo criterio que el auto-match del análisis).
      const monto = Number(montoRetenido || 0);
      const match = monto
        ? facturas.filter((f) => Math.abs(Number(f.pendiente || 0) - monto) < 0.5)
        : [];
      if (match.length === 1) {
        setSeleccionFactura((p) => ({ ...p, [k]: match[0].fact_id }));
      }
      if (!facturas.length) toast.info('El cliente no tiene facturas pendientes.');
    } catch (err) {
      toast.error('No se pudieron traer las facturas: ' + err.message);
    } finally {
      setCargandoFacturas((p) => ({ ...p, [k]: false }));
    }
  };

  const crearRetencion = async (doc, ret, idx) => {
    const k = keyOf(doc.load_doc_id, idx);
    const clieId = seleccionCliente[k];
    const factId = seleccionFactura[k] || null;
    if (!clieId) { toast.error('Seleccioná un cliente.'); return; }
    if ((ret.facturas || []).length > 0 && !factId) { toast.error('Seleccioná la factura a imputar.'); return; }
    if (!doc.tipo_retencion_id) { toast.error('El documento no tiene tipo de retención.'); return; }
    if (!ret.monto_retenido) { toast.error('La retención no tiene monto.'); return; }

    setEstados((p) => ({ ...p, [k]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/confirmar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          retenciones: [{
            load_doc_id: doc.load_doc_id,
            clie_id_clie: clieId,
            tipo_retencion_id: doc.tipo_retencion_id,
            factura_id: factId,
            cuit_agente: ret.cuit_agente,
            razon_social_agente: ret.razon_social_agente,
            numero_constancia: ret.numero_constancia,
            factura_asociada: ret.factura_asociada,
            base_imponible: ret.base_imponible,
            alicuota: ret.alicuota,
            monto_retenido: ret.monto_retenido,
            fecha_retencion: ret.fecha_retencion,
          }],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const r = (data.resultados || [])[0] || {};
      if (!r.ok) throw new Error(r.error || 'YiQi rechazó el recibo');

      setEstados((p) => ({ ...p, [k]: 'ok' }));
      setResultados((p) => ({ ...p, [k]: { cobro_id: r.cobro_id, emitido: r.emitido, emision_error: r.emision_error } }));
      const quien = ret.razon_social_agente || ret.cuit_agente;
      if (r.emitido) {
        toast.success(`Recibo #${r.cobro_id} creado y emitido (${quien}).`);
      } else {
        toast.warning(`Recibo #${r.cobro_id} creado pero NO se pudo emitir (${quien}): ${r.emision_error || 'error desconocido'}.`);
      }
      cargarPendientes();
    } catch (err) {
      setEstados((p) => ({ ...p, [k]: 'error' }));
      setResultados((p) => ({ ...p, [k]: { error: err.message } }));
      toast.error('Error: ' + err.message);
    }
  };

  // ---- Handlers del flujo OP ----
  const confirmarOp = async (op, { forzar = false } = {}) => {
    const id = op.load_doc_id;
    const datos = op.orden_pago || {};
    const clieId = opCliente[id];
    if (!clieId) { toast.error('Seleccioná el cliente en YiQi.'); return; }

    const rets = (datos.retenciones || []).map((r, idx) => ({
      tipo_retencion_id: opTipos[id]?.[idx],
      numero_certificado: String(opCerts[id]?.[idx] ?? r.numero_certificado ?? '').trim(),
      monto: r.monto,
      fecha: r.fecha || null,
    }));
    if (rets.length === 0) { toast.error('La OP no tiene retenciones para crear.'); return; }
    if (rets.some((r) => !r.tipo_retencion_id)) {
      toast.error('Confirmá el tipo de todas las retenciones antes de crear.');
      return;
    }
    if (rets.some((r) => !r.numero_certificado)) {
      toast.error('Completá el Nº de comprobante de todas las retenciones.');
      return;
    }

    setOpEstados((p) => ({ ...p, [id]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/confirmar-op`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          load_doc_id: id,
          clie_id_clie: clieId,
          cuit_agente: datos.cuit_agente,
          razon_social_agente: datos.razon_social_agente || '',
          fecha_pago: datos.fecha_pago || null,
          retenciones: rets,
          facturas_op: (datos.facturas || []).map((f) => f.numero_documento).filter(Boolean),
          emitir_recibo_id: opEmitir[id] || null,
          forzar,
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.detail && typeof data.detail === 'object') {
        setOpEstados((p) => ({ ...p, [id]: 'idle' }));
        const det = data.detail;
        if (det.codigo === 'retenciones_ya_cargadas') {
          const lista = (det.recibos || [])
            .map((r) => `#${r.nro_recibo || r.id} (${r.estado}, ${fmtMoney(r.total_retenciones)})`)
            .join(', ');
          if (window.confirm(`${det.mensaje}\n\nRecibos: ${lista}\n\n¿Crear el recibo IGUAL?`)) {
            confirmarOp(op, { forzar: true });
          }
          return;
        }
        toast.error(det.mensaje || 'YiQi rechazó la operación.');
        setOpResultados((p) => ({ ...p, [id]: { error: det.mensaje } }));
        setOpEstados((p) => ({ ...p, [id]: 'error' }));
        return;
      }
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail : `Error ${res.status}`;
        throw new Error(msg);
      }

      setOpEstados((p) => ({ ...p, [id]: 'ok' }));
      setOpResultados((p) => ({ ...p, [id]: data }));
      if (data.emitido) {
        toast.success(`Recibo #${data.cobro_id} creado y emitido (${datos.razon_social_agente || datos.cuit_agente}).`);
      } else {
        toast.warning(`Recibo #${data.cobro_id} creado pero NO se pudo emitir: ${data.emision_error || 'error desconocido'}.`);
      }
      cargarPendientes();
    } catch (err) {
      setOpEstados((p) => ({ ...p, [id]: 'error' }));
      setOpResultados((p) => ({ ...p, [id]: { error: err.message } }));
      toast.error('Error: ' + err.message);
    }
  };

  // Re-análisis de una OP con CUIT indicado a mano (cuando la extracción no lo resolvió).
  const reanalizarOp = async (op, cuit) => {
    const id = op.load_doc_id;
    const limpio = (cuit || '').replace(/\D/g, '');
    if (limpio.length !== 11) { toast.error('Ingresá un CUIT válido (11 dígitos).'); return; }
    setOpEstados((p) => ({ ...p, [id]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/analizar-op`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_doc_ids: [id], cuit_overrides: { [id]: limpio } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}`);
      const nueva = (data.ordenes || [])[0];
      if (!nueva) throw new Error(((data.errores || [])[0] || {}).error || 'No se pudo re-analizar el documento.');

      setOrdenes((prev) => prev.map((o) => (o.load_doc_id === id ? nueva : o)));
      setOpBusqueda((p) => ({ ...p, [id]: undefined }));
      // Se hace MERGE, no reemplazo: lo que el admin ya editó a mano (tipos y
      // Nº de comprobante) tiene prioridad sobre lo que vuelve del re-análisis.
      const tipos = {}; const certs = {};
      (nueva.orden_pago?.retenciones || []).forEach((r, idx) => {
        if (r.tipo_sugerido_id) tipos[idx] = r.tipo_sugerido_id;
        certs[idx] = r.numero_certificado || '';
      });
      setOpTipos((p) => ({ ...p, [id]: { ...tipos, ...(p[id] || {}) } }));
      setOpCerts((p) => {
        const previos = p[id] || {};
        const merged = { ...certs };
        Object.entries(previos).forEach(([idx, val]) => {
          if (String(val ?? '').trim()) merged[idx] = val;   // sólo pisa si el admin escribió algo
        });
        return { ...p, [id]: merged };
      });
      setOpCliente((p) => ({ ...p, [id]: nueva.cliente?.clie_id_clie || p[id] || '' }));
      const pend = (nueva.conciliacion?.medios?.candidatos || []).filter((c) => c.esta_codigo === 10);
      setOpEmitir((p) => ({
        ...p,
        [id]: nueva.conciliacion?.medios?.estado === 'pendiente' && pend.length === 1 ? pend[0].id : '',
      }));
      setOpEstados((p) => ({ ...p, [id]: 'idle' }));
      toast.success('OP re-analizada con el CUIT indicado.');
    } catch (err) {
      setOpEstados((p) => ({ ...p, [id]: 'idle' }));
      toast.error('Error al re-analizar: ' + err.message);
    }
  };

  // --- Datos para el formulario de carga manual --------------------------
  // Los dos endpoints ya existían (los usa el flujo de retenciones sueltas);
  // acá se reusan para que el formulario traiga solo todo lo que se pueda en
  // vez de hacer tipear al admin.
  const buscarClientePadron = useCallback(async (q) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/retenciones-clientes/buscar-cliente?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.resultados || [];
    } catch {
      return [];
    }
  }, [API_URL, token]);

  const facturasPorCuit = useCallback(async (cuit) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/retenciones-clientes/facturas-cliente?cuit=${encodeURIComponent(cuit)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.facturas || [];
    } catch {
      return [];
    }
  }, [API_URL, token]);

  // Carga manual de una OP cuyo PDF no se pudo leer. Manda los datos que
  // completó el admin y recibe la MISMA tarjeta que devuelve el análisis
  // automático (cliente resuelto, facturas pendientes, conciliación), así el
  // resto del flujo —confirmar, emitir, conciliar— no cambia en nada.
  const cargarOpManual = async (op, datos) => {
    const id = op.load_doc_id;
    setOpEstados((p) => ({ ...p, [id]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/analizar-op-manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_doc_id: id, ...datos }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}`);
      const nueva = data.orden;
      if (!nueva) throw new Error('El servidor no devolvió la orden de pago.');

      setOrdenes((prev) => prev.map((o) => (o.load_doc_id === id ? nueva : o)));

      // Se siembran los controles de la tarjeta con lo que quedó cargado, para
      // que el admin no tenga que volver a elegir tipo ni reescribir el Nº.
      const tipos = {}; const certs = {};
      (nueva.orden_pago?.retenciones || []).forEach((r, idx) => {
        if (r.tipo_sugerido_id) tipos[idx] = r.tipo_sugerido_id;
        certs[idx] = r.numero_certificado || '';
      });
      setOpTipos((p) => ({ ...p, [id]: tipos }));
      setOpCerts((p) => ({ ...p, [id]: certs }));
      setOpCliente((p) => ({ ...p, [id]: nueva.cliente?.clie_id_clie || '' }));
      const pend = (nueva.conciliacion?.medios?.candidatos || []).filter((c) => c.esta_codigo === 10);
      setOpEmitir((p) => ({
        ...p,
        [id]: nueva.conciliacion?.medios?.estado === 'pendiente' && pend.length === 1 ? pend[0].id : '',
      }));
      setOpBusqueda((p) => ({ ...p, [id]: undefined }));
      setOpEstados((p) => ({ ...p, [id]: 'idle' }));
      toast.success('Orden de pago cargada. Revisá las facturas y la conciliación antes de crear.');
    } catch (err) {
      setOpEstados((p) => ({ ...p, [id]: 'idle' }));
      toast.error('Error al cargar los datos: ' + err.message);
    }
  };

  // Búsqueda manual del cliente de una OP por nombre o CUIT (vía de escape
  // cuando el CUIT del PDF está mal impreso o el cliente figura con otro CUIT
  // en YiQi). Al elegir uno se re-analiza con SU CUIT, así el pipeline vuelve a
  // traer facturas y conciliación coherentes.
  const buscarClienteOp = async (op, q) => {
    const id = op.load_doc_id;
    if (!q || q.trim().length < 2) { toast.info('Ingresá CUIT o nombre (mín. 2).'); return; }
    try {
      const res = await fetch(
        `${API_URL}/api/admin/retenciones-clientes/buscar-cliente?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const resultados = data.resultados || [];
      setOpBusqueda((p) => ({ ...p, [id]: resultados }));
      if (!resultados.length) toast.info('Sin resultados en el padrón de clientes de YiQi.');
    } catch (err) {
      toast.error('Error en la búsqueda: ' + err.message);
    }
  };

  const conciliarOp = async (op) => {
    const id = op.load_doc_id;
    const recibos = [
      ...(op.conciliacion?.medios?.candidatos || []),
      ...(op.conciliacion?.retenciones?.recibos || []),
    ].map((c) => c.id).filter(Boolean);
    if (!window.confirm('¿Marcar esta orden de pago como CONCILIADA? No se crea nada en YiQi: la OP ya está aplicada.')) return;

    setOpEstados((p) => ({ ...p, [id]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/retenciones-clientes/conciliar-op`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_doc_id: id, recibo_ids: [...new Set(recibos)], motivo: '' }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail : `Error ${res.status}`;
        throw new Error(msg);
      }
      setOpEstados((p) => ({ ...p, [id]: 'conciliado' }));
      toast.success('Orden de pago conciliada.');
      cargarPendientes();
    } catch (err) {
      setOpEstados((p) => ({ ...p, [id]: 'error' }));
      toast.error('Error al conciliar: ' + err.message);
    }
  };

  const todosSel = pendientes.length > 0 && seleccionados.size === pendientes.length;

  return (
    <div className="text-gray-100">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <ReceiptText className="w-7 h-7 text-[#00a1df]" />
              OP + Retenciones
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Procesá los PDFs de retenciones cargados y generá el recibo en YiQi
            </p>
          </div>
          <button
            onClick={cargarPendientes}
            className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${cargandoPend ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>

        {/* Documentos pendientes */}
        <div ref={listaRef} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl scroll-mt-4">
          <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between gap-3">
            <button
              onClick={() => setListaAbierta((v) => !v)}
              title={listaAbierta ? 'Plegar la lista' : 'Mostrar la lista'}
              className="flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-gray-200 uppercase tracking-widest transition-colors min-w-0"
            >
              {listaAbierta
                ? <ChevronDown className="w-4 h-4 shrink-0 text-[#00a1df]" />
                : <ChevronRight className="w-4 h-4 shrink-0 text-[#00a1df]" />}
              <FileText className="w-4 h-4 text-[#00a1df] shrink-0" />
              <span className="truncate">Documentos pendientes</span>
              <span className="text-[11px] font-medium normal-case tracking-normal text-gray-500 shrink-0">
                ({pendientes.length})
              </span>
            </button>
            <div className="flex items-center gap-4">
              {!listaAbierta && analizadosIds.size > 0 && (
                <span className="hidden sm:inline text-[11px] text-[#00a1df]">
                  {analizadosIds.size} en análisis
                </span>
              )}
              {listaAbierta && (
                <span className="text-[11px] text-gray-500">{seleccionados.size} seleccionado(s)</span>
              )}
              {!listaAbierta && (
                <button
                  onClick={() => setListaAbierta(true)}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-[#00a1df] hover:text-[#4fc3f7] transition-colors"
                >
                  <ListFilter className="w-3.5 h-3.5" /> Mostrar lista
                </button>
              )}
              {/* Las acciones operan sobre la selección: con la lista plegada no
                  se ve qué está seleccionado, así que se ocultan. */}
              {listaAbierta && puedeDescartar && (
                <button
                  onClick={() => descartar(Array.from(seleccionados))}
                  disabled={descartando || seleccionados.size === 0}
                  title="Descartar los documentos seleccionados (limpiar listado)"
                  className="flex items-center gap-2 bg-red-600/90 hover:bg-red-600 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95"
                >
                  {descartando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {descartando ? 'DESCARTANDO…' : 'DESCARTAR'}
                </button>
              )}
              {listaAbierta && (
                <button
                  onClick={analizar}
                  disabled={analizando || seleccionados.size === 0}
                  className="flex items-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95"
                >
                  {analizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                  {analizando ? 'ANALIZANDO…' : 'ANALIZAR'}
                </button>
              )}
            </div>
          </div>

          {!listaAbierta ? null : pendientes.length === 0 ? (
            <div className="py-14 text-center text-gray-500 text-sm">
              {cargandoPend ? 'Cargando…' : 'No hay documentos pendientes.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 uppercase tracking-wider text-[10px] border-b border-gray-700/50">
                  <th className="py-2 px-4 text-left w-10">
                    <input type="checkbox" checked={todosSel} onChange={toggleTodos} className="accent-[#00a1df]" />
                  </th>
                  <th className="py-2 px-2 text-left font-bold">Cliente</th>
                  <th className="py-2 px-2 text-left font-bold">Archivo</th>
                  <th className="py-2 px-2 text-left font-bold">Tipo retención</th>
                  <th className="py-2 px-2 text-left font-bold">Fecha carga</th>
                  {puedeDescartar && <th className="py-2 px-2 w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {pendientes.map((d) => (
                  <tr
                    key={d.id}
                    className={`transition-colors ${
                      analizadosIds.has(d.id)
                        ? 'bg-[#00a1df]/5 hover:bg-[#00a1df]/10'
                        : 'hover:bg-gray-700/20'
                    }`}
                  >
                    <td className="py-2.5 px-4">
                      <input type="checkbox" checked={seleccionados.has(d.id)} onChange={() => toggleSel(d.id)} className="accent-[#00a1df]" />
                    </td>
                    <td className="py-2.5 px-2 font-semibold text-white truncate max-w-[180px]">
                      {d.cliente_nombre || '—'}
                      {analizadosIds.has(d.id) && (
                        <span
                          title="Este documento está en el análisis de abajo"
                          className="ml-2 align-middle inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide text-[#00a1df] bg-[#00a1df]/10 border border-[#00a1df]/30"
                        >
                          en análisis
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 max-w-[220px]">
                      <button
                        onClick={() => abrirVisor(d.id, d.archivo_nombre)}
                        title="Ver PDF"
                        className="inline-flex items-center gap-1.5 text-gray-400 hover:text-[#00a1df] transition-colors truncate max-w-full"
                      >
                        <Eye className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{d.archivo_nombre}</span>
                      </button>
                    </td>
                    <td className="py-2.5 px-2">
                      {d.tipo_documento === 'orden_pago' ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            title={d.tipo_documento_deteccion?.evidencia
                              ? `Detectada automáticamente. Texto en el PDF: "${d.tipo_documento_deteccion.evidencia}"`
                              : 'Detectada automáticamente al subir el archivo'}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/20"
                          >
                            <FileText className="w-3 h-3" /> Orden de pago
                          </span>
                          {isAdmin && (
                            <button
                              onClick={() => corregirDocumento(d)}
                              title="El detector se equivocó: cambiar a Retención"
                              className="w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-[#00a1df] hover:bg-[#00a1df]/10 transition-colors"
                            >
                              <ArrowLeftRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ) : editandoTipo === d.id ? (
                        <div className="flex items-center gap-1">
                          <select
                            value={tipoEditValor}
                            onChange={(e) => setTipoEditValor(e.target.value)}
                            className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00a1df]"
                          >
                            <option value="">— Tipo —</option>
                            {tiposRetencion.map((t) => (
                              <option key={t.id} value={t.id}>{t.nombre}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => guardarTipo(d.id)}
                            disabled={guardandoTipo}
                            title="Guardar"
                            className="w-7 h-7 inline-flex items-center justify-center rounded-full text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                          >
                            {guardandoTipo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={cancelarEdicionTipo}
                            title="Cancelar"
                            className="w-7 h-7 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-600/30 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-[#00a1df] bg-[#00a1df]/10">
                            <Percent className="w-3 h-3" /> {d.tipo_retencion_nombre || '—'}
                          </span>
                          {d.tipo_alerta && d.tipo_alerta.override && !d.tipo_alerta.resuelto && (
                            <span
                              title={`El usuario mantuvo "${d.tipo_alerta.tipo_elegido_nombre}" pese a que el sistema detectó "${d.tipo_alerta.tipo_detectado_nombre}".${d.tipo_alerta.evidencia ? ` Texto en el PDF: "${d.tipo_alerta.evidencia}"` : ''}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/30"
                            >
                              <AlertTriangle className="w-3 h-3" /> Debería ser {d.tipo_alerta.tipo_detectado_nombre}
                            </span>
                          )}
                          {d.tipo_alerta && d.tipo_alerta.resuelto && (
                            <span
                              title={`Corregido por ${d.tipo_alerta.corregido_por || ''}`}
                              className="inline-flex items-center gap-1 text-[11px] text-green-400"
                            >
                              <Check className="w-3 h-3" /> corregido
                            </span>
                          )}
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => abrirEdicionTipo(d)}
                                title="Corregir el tipo de retención"
                                className="w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-[#00a1df] hover:bg-[#00a1df]/10 transition-colors"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => corregirDocumento(d)}
                                title="El detector se equivocó: cambiar a Orden de pago"
                                className="w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                              >
                                <ArrowLeftRight className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-gray-500 text-xs">{fmtFecha(d.created_at)}</td>
                    {puedeDescartar && (
                      <td className="py-2.5 px-2 text-right">
                        <button
                          onClick={() => descartar([d.id])}
                          disabled={descartando}
                          title="Descartar este documento (sacarlo del listado)"
                          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Error global */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <p className="text-red-300/90 text-sm">{error}</p>
          </div>
        )}

        {/* ── Bloque del análisis ──────────────────────────────────────────
            Ancla del auto-scroll. La barra de arriba es la confirmación
            persistente de que el análisis terminó (el toast se va solo) y la
            vía de vuelta a la lista. */}
        <div
          ref={resultadosRef}
          className={`space-y-6 scroll-mt-4 rounded-xl transition-shadow duration-500 ${
            destacar ? 'ring-2 ring-[#00a1df]/60' : 'ring-0 ring-transparent'
          }`}
        >
          {/* `top-4` acompaña al `scroll-mt-4` del contenedor: el auto-scroll deja
              el bloque 1rem bajo el borde y la barra queda justo ahí. El fondo es
              opaco a propósito: se superpone al contenido que pasa por debajo. */}
          {hayResultados && (
            <div className="sticky top-4 z-20 flex flex-wrap items-center justify-between gap-3 bg-[#12331f] border border-green-500/30 rounded-xl px-4 py-3 shadow-xl">
              <p className="text-sm font-bold text-green-300 flex items-center gap-2 min-w-0">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  {documentos.length + ordenes.length} documento(s) analizado(s)
                  {ordenes.length > 0 && ` · ${ordenes.length} OP`}
                </span>
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => volverALista()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-200 bg-gray-700/70 hover:bg-gray-600 transition-colors"
                >
                  <ListFilter className="w-3.5 h-3.5" /> Volver a la lista
                </button>
                <button
                  onClick={() => volverALista({ limpiar: true })}
                  title="Cerrar este análisis (no borra nada: los documentos siguen pendientes)"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-400 hover:text-gray-200 hover:bg-gray-700/70 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Cerrar análisis
                </button>
              </div>
            </div>
          )}

          {/* Errores por documento */}
          {errores.length > 0 && (
            <div className="bg-amber-900/10 border border-amber-500/30 rounded-xl p-4 space-y-1">
              {errores.map((e, i) => (
                <p key={i} className="text-xs text-amber-300/90">
                  <span className="font-mono">{e.archivo || e.load_doc_id}</span>: {String(e.error)}
                </p>
              ))}
            </div>
          )}

          {/* Resultados del análisis */}
          {documentos.map((doc) => (
            <div key={doc.load_doc_id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-white truncate">{doc.archivo_nombre}</h3>
                  <p className="text-[11px] text-gray-500">
                    {doc.cliente_nombre} · <span className="text-[#00a1df]">{doc.tipo_retencion_nombre}</span>
                    {doc.tipo_alerta && doc.tipo_alerta.override && !doc.tipo_alerta.resuelto && (
                      <span
                        title={`El usuario mantuvo "${doc.tipo_alerta.tipo_elegido_nombre}" pese a que el sistema detectó "${doc.tipo_alerta.tipo_detectado_nombre}".${doc.tipo_alerta.evidencia ? ` Texto en el PDF: "${doc.tipo_alerta.evidencia}"` : ''}`}
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/30 align-middle"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" /> Debería ser {doc.tipo_alerta.tipo_detectado_nombre}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] text-gray-500">{doc.retenciones?.length || 0} retención(es)</span>
                  <button
                    onClick={() => abrirVisor(doc.load_doc_id, doc.archivo_nombre)}
                    title="Ver PDF"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-[#00a1df] bg-[#00a1df]/10 hover:bg-[#00a1df]/20 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Ver PDF
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-700/50">
                {(doc.retenciones || []).map((ret, idx) => {
                  const k = keyOf(doc.load_doc_id, idx);
                  const estado = estados[k] || 'idle';
                  const resultado = resultados[k];
                  const candidatos = busquedaManual[k] || ret.candidatos || [];
                  const noEncontrado = ret.estado_cliente === 'no_encontrado' && !(busquedaManual[k] || []).length;
                  const facturas = ret.facturas || [];
                  const facturaPendiente = facturas.length > 0 && !seleccionFactura[k];

                  return (
                    <div key={k} className="px-6 py-4 space-y-3">
                      {/* Datos de la retención */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <Campo label="Agente (cliente)" value={ret.razon_social_agente || '—'} />
                        <Campo label="CUIT agente" value={ret.cuit_agente || '— (revisar)'} mono />
                        <Campo label="Monto retenido" value={fmtMoney(ret.monto_retenido)} strong />
                        <Campo label="Fecha" value={fmtFecha(ret.fecha_retencion)} />
                        <Campo label="Base imponible" value={fmtMoney(ret.base_imponible)} />
                        <Campo label="Alícuota" value={ret.alicuota != null ? `${ret.alicuota}%` : '—'} />
                        <Campo label="Comprobante en PDF" value={ret.factura_asociada || '—'} mono />
                        <Campo label="Nº constancia" value={ret.numero_constancia || '—'} mono />
                      </div>

                      {/* Factura a imputar */}
                      {facturas.length > 0 ? (
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
                            Factura a imputar
                            {ret.estado_factura === 'seleccion' && (
                              <span className="text-amber-400 normal-case tracking-normal">
                                {' '}· el comprobante del PDF ({ret.factura_asociada || '—'}) no coincide con ninguna factura, elegila
                              </span>
                            )}
                            {ret.estado_factura === 'auto' && (
                              <span className="text-green-400 normal-case tracking-normal"> · coincidió con el PDF</span>
                            )}
                          </p>
                          <select
                            value={seleccionFactura[k] || ''}
                            onChange={(e) => setSeleccionFactura((p) => ({ ...p, [k]: Number(e.target.value) }))}
                            disabled={estado === 'ok' || estado === 'loading'}
                            className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
                          >
                            <option value="">— Elegir factura —</option>
                            {facturas.map((f) => (
                              <option key={f.fact_id} value={f.fact_id}>
                                {f.tipo} {f.numero} · {fmtFecha(f.fecha)} · pend. {fmtMoney(f.pendiente)} / total {fmtMoney(f.total)}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : cargandoFacturas[k] ? (
                        <p className="text-xs text-[#00a1df] flex items-center gap-1.5">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Buscando facturas pendientes del cliente…
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">Sin facturas pendientes del cliente (se creará sin imputar).</p>
                      )}

                      {/* Resolución de cliente + acción */}
                      <div className="flex flex-col md:flex-row md:items-center gap-3 pt-1">
                        <div className="flex-1 min-w-0">
                          {candidatos.length > 0 ? (
                            <select
                              value={seleccionCliente[k] || ''}
                              onChange={(e) => {
                                const nuevoId = Number(e.target.value);
                                setSeleccionCliente((p) => ({ ...p, [k]: nuevoId }));
                                // Las facturas dependen del cliente: al cambiarlo
                                // hay que volver a pedirlas (el smartie las busca
                                // por ID de cliente, no por CUIT).
                                setSeleccionFactura((p) => ({ ...p, [k]: null }));
                                if (nuevoId) {
                                  cargarFacturasCliente(
                                    doc.load_doc_id, idx, nuevoId, ret.monto_retenido
                                  );
                                }
                              }}
                              disabled={estado === 'ok' || estado === 'loading'}
                              className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
                            >
                              <option value="">— Elegir cliente en YiQi —</option>
                              {candidatos.map((c) => (
                                <option key={c.clie_id_clie} value={c.clie_id_clie}>
                                  {c.razon_social} (ID {c.clie_id_clie} · CUIT {c.cuit})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="flex items-center gap-1.5 text-xs text-amber-400">
                              <UserX className="w-3.5 h-3.5" /> Sin cliente en YiQi — buscá manualmente
                            </span>
                          )}
                        </div>

                        {/* Búsqueda manual */}
                        <ManualSearch onSearch={(q) => buscarManual(k, q)} disabled={estado === 'ok'} />

                        {/* Estado */}
                        <div className="min-w-[120px]">
                          {estado === 'ok' && resultado && (
                            resultado.emitido ? (
                              <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Recibo #{resultado.cobro_id} · Emitido
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-amber-400 font-bold" title={resultado.emision_error}>
                                <AlertCircle className="w-3.5 h-3.5" /> Recibo #{resultado.cobro_id} · sin emitir
                              </span>
                            )
                          )}
                          {estado === 'error' && resultado && (
                            <span className="flex items-center gap-1 text-xs text-red-400 font-bold truncate" title={resultado.error}>
                              <XCircle className="w-3.5 h-3.5" /> Error
                            </span>
                          )}
                        </div>

                        {/* Botón crear */}
                        <button
                          onClick={() => crearRetencion(doc, ret, idx)}
                          disabled={estado === 'loading' || estado === 'ok' || noEncontrado || !seleccionCliente[k] || facturaPendiente}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 whitespace-nowrap ${
                            estado === 'ok'
                              ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                              : estado === 'loading'
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : (noEncontrado || !seleccionCliente[k] || facturaPendiente)
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : estado === 'error'
                              ? 'bg-red-600 hover:bg-red-500 text-white'
                              : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
                          }`}
                        >
                          {estado === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : estado === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" />
                            : <ReceiptText className="w-3.5 h-3.5" />}
                          {estado === 'ok' ? 'Creado' : estado === 'error' ? 'Reintentar' : 'Crear recibo'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Resultados del análisis de ÓRDENES DE PAGO */}
          {ordenes.map((op) => (
            <OrdenPagoCard
              key={op.load_doc_id}
              op={op}
              tiposRetencion={tiposRetencion}
              estado={opEstados[op.load_doc_id] || 'idle'}
              resultado={opResultados[op.load_doc_id]}
              tipos={opTipos[op.load_doc_id] || {}}
              setTipo={(idx, val) => setOpTipos((p) => ({
                ...p, [op.load_doc_id]: { ...(p[op.load_doc_id] || {}), [idx]: val },
              }))}
              certs={opCerts[op.load_doc_id] || {}}
              setCert={(idx, val) => setOpCerts((p) => ({
                ...p, [op.load_doc_id]: { ...(p[op.load_doc_id] || {}), [idx]: val },
              }))}
              clienteSel={opCliente[op.load_doc_id] || ''}
              setClienteSel={(val) => setOpCliente((p) => ({ ...p, [op.load_doc_id]: val }))}
              emitirSel={opEmitir[op.load_doc_id] || ''}
              setEmitirSel={(val) => setOpEmitir((p) => ({ ...p, [op.load_doc_id]: val }))}
              onConfirmar={() => confirmarOp(op)}
              onConciliar={() => conciliarOp(op)}
              onReanalizar={(cuit) => reanalizarOp(op, cuit)}
              onBuscarCliente={(q) => buscarClienteOp(op, q)}
              resultadosBusqueda={opBusqueda[op.load_doc_id]}
              onVerPdf={() => abrirVisor(op.load_doc_id, op.archivo_nombre)}
              onCargarManual={(datos) => cargarOpManual(op, datos)}
              buscarCliente={buscarClientePadron}
              traerFacturas={facturasPorCuit}
            />
          ))}
        </div>
        {/* ── fin del bloque del análisis ─────────────────────────────────── */}

        {/* Info footer */}
        {documentos.length > 0 && (
          <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-4 flex gap-3 text-sm text-blue-300/80">
            <Hash className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
            <p>
              Cada retención se crea como recibo en YiQi (entidad <strong className="text-blue-300">COBRO</strong>, Schema <strong className="text-blue-300">1383</strong>, Punto de venta <strong className="text-blue-300">7</strong>).
              El tipo de retención viene del documento cargado. El CUIT del agente (cliente) se busca por el smartie de clientes; nunca se usa el CUIT propio de CDSur.
            </p>
          </div>
        )}
      </div>

      {/* Visor de PDF en modal */}
      {visor.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={cerrarVisor}
        >
          <div
            className="bg-gray-900 rounded-xl shadow-2xl border border-gray-700 w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            style={dragVisor.panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-3 border-b border-gray-700 bg-gray-800/50"
              {...dragVisor.handleProps}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-[#00a1df] shrink-0" />
                <h3 className="text-sm font-bold text-white truncate">{visor.nombre}</h3>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {visor.url && (
                  <a
                    href={visor.url}
                    download={visor.nombre}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
                    title="Descargar"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={cerrarVisor}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-800 min-h-0">
              {visor.cargando || !visor.url ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin text-[#00a1df] mb-2" />
                  <p className="text-xs">Cargando documento…</p>
                </div>
              ) : (
                <iframe
                  src={visor.url}
                  title={visor.nombre}
                  className={`w-full h-full border-0 ${dragVisor.dragging ? 'pointer-events-none' : ''}`}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Campo = ({ label, value, mono, strong }) => (
  <div className="min-w-0">
    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`truncate ${mono ? 'font-mono' : ''} ${strong ? 'text-white font-bold' : 'text-gray-300'}`} title={String(value)}>
      {value}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Tarjeta de Orden de Pago (flujo OP)
// ---------------------------------------------------------------------------
const ESTADO_FACTURA_LABEL = {
  auto: { texto: 'coincide', cls: 'text-green-400' },
  monto_difiere: { texto: 'monto difiere', cls: 'text-amber-400' },
  ambigua: { texto: 'ambigua', cls: 'text-amber-400' },
  no_encontrada: { texto: 'no encontrada', cls: 'text-red-400' },
};

// Por qué no se pudo resolver el cliente (lo informa el backend en cliente.motivo).
const MOTIVO_CLIENTE = {
  sin_cuit: 'No se pudo leer el CUIT del cliente en el PDF. Indicalo a mano o buscá al cliente por nombre.',
  cuit_invalido: 'El CUIT leído del PDF no es válido (o es el de CDSur). Indicá el del cliente.',
  cuit_sin_cliente: 'El CUIT no figura en el padrón de clientes de YiQi. Verificá el número o buscá al cliente por nombre.',
  error_yiqi: 'No se pudo consultar el padrón de clientes de YiQi. Reintentá en unos minutos.',
};

const MEDIOS_ESTADO_LABEL = {
  emitido: { texto: 'Medios de pago ya aplicados (recibo Emitido)', cls: 'text-green-400' },
  pendiente: { texto: 'Recibo de medios PENDIENTE: se emitirá antes de crear', cls: 'text-amber-400' },
  sin_recibo: { texto: 'Los valores todavía no están cargados en ningún recibo', cls: 'text-red-400' },
};

const OrdenPagoCard = ({
  op, tiposRetencion, estado, resultado, tipos, setTipo, certs, setCert,
  clienteSel, setClienteSel, emitirSel, setEmitirSel,
  onConfirmar, onConciliar, onReanalizar, onBuscarCliente, resultadosBusqueda, onVerPdf,
  onCargarManual, buscarCliente, traerFacturas,
}) => {
  const datos = op.orden_pago || {};
  const val = op.validaciones || {};
  const conc = op.conciliacion || {};
  const cliente = op.cliente || {};
  const medios = conc.medios || {};
  const retConc = conc.retenciones || {};
  const accion = conc.accion_sugerida || 'crear';
  const bloqueado = estado === 'ok' || estado === 'conciliado' || estado === 'loading';

  // El PDF no se pudo leer (o la extracción no sacó nada): la tarjeta se abre
  // como formulario en vez de mostrar tablas vacías. Al cargarlo, el backend
  // devuelve la tarjeta normal y esto se apaga solo.
  // El toggle existe para que nunca sea un callejón sin salida: el admin puede
  // mirar la tarjeta con lo poco que se haya leído, y volver al formulario.
  const necesitaCarga = Boolean(op.carga_manual?.requerida);
  const [verTarjeta, setVerTarjeta] = useState(false);
  const modoCargaManual = necesitaCarga && !verTarjeta;

  const candidatosPend = (medios.candidatos || []).filter((c) => c.esta_codigo === 10);
  const requiereEmitir = medios.estado === 'pendiente';
  const tiposCompletos = (datos.retenciones || []).every((_, idx) => tipos[idx]);
  const certsCompletos = (datos.retenciones || []).every((_, idx) => String(certs[idx] ?? '').trim());
  const puedeCrear =
    !bloqueado && clienteSel && tiposCompletos && certsCompletos &&
    (datos.retenciones || []).length > 0 &&
    accion !== 'esperar' && accion !== 'sin_retenciones' &&
    (!requiereEmitir || emitirSel);

  return (
    <div className="bg-gray-800 border border-emerald-500/20 rounded-xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-white truncate flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 shrink-0">
              ORDEN DE PAGO
            </span>
            <span className="truncate">{op.archivo_nombre}</span>
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {datos.razon_social_agente || op.cliente_nombre}
            {datos.cuit_agente && (
              <span
                className="font-mono"
                title={datos.cuit_origen === 'texto_pdf'
                  ? 'CUIT resuelto por backstop: único CUIT distinto de CDSur en el texto del PDF'
                  : datos.cuit_origen === 'manual' ? 'CUIT indicado manualmente' : undefined}
              > · CUIT {datos.cuit_agente}{datos.cuit_origen === 'texto_pdf' ? '*' : ''}</span>
            )}
            {!datos.cuit_agente && <span className="text-amber-400"> · CUIT sin resolver</span>}
            {datos.numero_op && <span> · OP {datos.numero_op}</span>}
            {datos.fecha_pago && <span> · {fmtFecha(datos.fecha_pago)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] text-gray-500 uppercase">Total pago</p>
            <p className="text-sm font-bold text-white">{fmtMoney(datos.total_pago)}</p>
          </div>
          <button
            onClick={onVerPdf}
            title="Ver PDF"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-[#00a1df] bg-[#00a1df]/10 hover:bg-[#00a1df]/20 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" /> Ver PDF
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {modoCargaManual ? (
          <>
            <CargaManualOp
              op={op}
              tiposRetencion={tiposRetencion}
              onCargar={onCargarManual}
              disabled={estado === 'loading'}
              buscarCliente={buscarCliente}
              traerFacturas={traerFacturas}
            />
            <button
              type="button"
              onClick={() => setVerTarjeta(true)}
              className="text-[11px] text-gray-500 hover:text-gray-300 underline"
            >
              Ver la tarjeta con lo que sí se pudo leer
            </button>
          </>
        ) : (
        <>
        {necesitaCarga && (
          <div className="bg-amber-900/15 border border-amber-500/40 rounded-lg p-3 flex items-center justify-between gap-3">
            <p className="text-[11px] text-amber-300/90">
              {op.carga_manual?.mensaje || 'Faltan datos de esta orden de pago.'}
            </p>
            <button
              type="button"
              onClick={() => setVerTarjeta(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 transition-colors shrink-0"
            >
              <Pencil className="w-3.5 h-3.5" /> Completar a mano
            </button>
          </div>
        )}

        {/* Validación aritmética */}
        {!val.ok && (
          <div className="bg-red-900/20 border border-red-500/40 rounded-lg p-3 space-y-1">
            <p className="text-xs font-bold text-red-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Los números de la OP no cierran — revisá el PDF antes de continuar
            </p>
            {(val.mensajes || []).map((m, i) => (
              <p key={i} className="text-[11px] text-red-300/80">{m}</p>
            ))}
          </div>
        )}

        {/* Totales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Campo label="Σ Facturas (bruto)" value={fmtMoney(val.suma_facturas)} strong />
          <Campo label="Σ Retenciones" value={fmtMoney(val.suma_retenciones)} strong />
          <Campo label="Σ Medios de pago" value={fmtMoney(val.suma_medios)} />
          <Campo label="Saldo disponible hoy" value={fmtMoney(op.saldo_disponible)} />
        </div>

        {/* Facturas de la OP */}
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">Facturas de la orden de pago</p>
          <div className="rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 uppercase text-[9px] bg-gray-900/40">
                  <th className="py-1.5 px-3 text-left">Nº en la OP</th>
                  <th className="py-1.5 px-3 text-right">Importe OP</th>
                  <th className="py-1.5 px-3 text-left">Factura YiQi</th>
                  <th className="py-1.5 px-3 text-right">Pendiente</th>
                  <th className="py-1.5 px-3 text-left">Match</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/40">
                {(datos.facturas || []).map((f, i) => {
                  const est = ESTADO_FACTURA_LABEL[f.estado] || ESTADO_FACTURA_LABEL.no_encontrada;
                  return (
                    <tr key={i}>
                      <td className="py-1.5 px-3 font-mono text-gray-300">{f.numero_documento || '—'}</td>
                      <td className="py-1.5 px-3 text-right text-gray-300">{fmtMoney(f.importe)}</td>
                      <td className="py-1.5 px-3 text-gray-400">
                        {f.match ? `${f.match.tipo || ''} ${f.match.numero} · ${fmtFecha(f.match.fecha)}` : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right text-gray-400">
                        {f.match ? fmtMoney(f.match.pendiente) : '—'}
                      </td>
                      <td className={`py-1.5 px-3 font-bold ${est.cls}`}>{est.texto}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {op.factura_imputacion && (
            <p className="text-[11px] text-emerald-400 mt-1.5">
              Retenciones → factura <span className="font-mono">{op.factura_imputacion.numero}</span>
              {' '}(pendiente {fmtMoney(op.factura_imputacion.pendiente)}, coincide con Σ retenciones).
            </p>
          )}
        </div>

        {/* Retenciones (una por certificado, tipo confirmado por el admin) */}
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">
            Retenciones (una por certificado) — confirmá el tipo antes de crear
          </p>
          <div className="rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 uppercase text-[9px] bg-gray-900/40">
                  <th className="py-1.5 px-3 text-left">Texto en la OP</th>
                  <th className="py-1.5 px-3 text-left">Certificado</th>
                  <th className="py-1.5 px-3 text-right">Base</th>
                  <th className="py-1.5 px-3 text-right">Monto</th>
                  <th className="py-1.5 px-3 text-left w-52">Tipo (catálogo)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/40">
                {(datos.retenciones || []).map((r, idx) => (
                  <tr key={idx}>
                    <td className="py-1.5 px-3 text-gray-300">{r.tipo_texto || '—'}</td>
                    <td className="py-1.5 px-3">
                      <input
                        value={certs[idx] ?? (r.numero_certificado || '')}
                        onChange={(e) => setCert(idx, e.target.value)}
                        disabled={bloqueado}
                        placeholder="Nº comprobante"
                        className={`w-full bg-gray-900 border rounded-md px-2 py-1 text-xs font-mono text-white focus:outline-none disabled:opacity-50 ${
                          String(certs[idx] ?? r.numero_certificado ?? '').trim() ? 'border-gray-600 focus:border-[#00a1df]' : 'border-amber-500/60'
                        }`}
                      />
                    </td>
                    <td className="py-1.5 px-3 text-right text-gray-400">{fmtMoney(r.base_imponible)}</td>
                    <td className="py-1.5 px-3 text-right font-bold text-white">{fmtMoney(r.monto)}</td>
                    <td className="py-1.5 px-3">
                      <select
                        value={tipos[idx] || ''}
                        onChange={(e) => setTipo(idx, Number(e.target.value) || '')}
                        disabled={bloqueado}
                        className={`w-full bg-gray-900 border rounded-md px-2 py-1 text-xs text-white focus:outline-none disabled:opacity-50 ${
                          tipos[idx] ? 'border-gray-600 focus:border-[#00a1df]' : 'border-amber-500/60'
                        }`}
                      >
                        <option value="">— Confirmar tipo —</option>
                        {tiposRetencion.map((t) => (
                          <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                      </select>
                      {r.tipo_sugerido_nombre && (
                        <p className="text-[10px] text-gray-500 mt-0.5">sugerido: {r.tipo_sugerido_nombre}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Medios de pago del PDF (solo para matchear: nunca se escriben) */}
        {(datos.medios_pago || []).length > 0 && (
          <p className="text-[11px] text-gray-500">
            Medios de pago en la OP:{' '}
            {(datos.medios_pago || []).map((m, i) => (
              <span key={i} className="text-gray-400">
                {i > 0 && ' · '}
                {m.tipo.replace('_', ' ')} {m.numero && `#${m.numero}`} {m.banco && `(${m.banco})`}{' '}
                {fmtMoney(m.importe)}{m.fecha_vencimiento && ` vto ${fmtFecha(m.fecha_vencimiento)}`}
              </span>
            ))}
          </p>
        )}

        {/* Conciliación por componentes */}
        <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3 space-y-2">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Conciliación contra recibos existentes</p>

          {/* Componente medios */}
          <p className={`text-xs font-bold ${(MEDIOS_ESTADO_LABEL[medios.estado] || {}).cls || 'text-gray-400'}`}>
            {(MEDIOS_ESTADO_LABEL[medios.estado] || {}).texto || '—'}
          </p>
          {(medios.candidatos || []).length > 0 && (
            <div className="text-[11px] text-gray-400 space-y-0.5">
              {(medios.candidatos || []).map((c) => (
                <p key={c.id}>
                  Recibo {c.nro_recibo ? `#${c.nro_recibo}` : `id ${c.id}`} · {c.estado} ·
                  cheques {fmtMoney(c.total_cheques)} · transf. {fmtMoney(c.total_txs)}
                  {c.fecha_ultima_acreditacion && ` · acred. ${fmtFecha(c.fecha_ultima_acreditacion)}`}
                </p>
              ))}
            </div>
          )}

          {/* Emisión del pendiente (confirmación explícita del admin) */}
          {requiereEmitir && (
            <div className="pt-1">
              <p className="text-[11px] text-amber-300 mb-1">
                Un recibo Pendiente no descuenta saldo: hay que emitirlo antes de crear el recibo de retenciones.
                Confirmá cuál emitir (solo cambia el estado, no se modifica su contenido):
              </p>
              <select
                value={emitirSel || ''}
                onChange={(e) => setEmitirSel(Number(e.target.value) || '')}
                disabled={bloqueado}
                className="w-full md:w-auto bg-gray-900 border border-amber-500/50 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
              >
                <option value="">— Elegir recibo a emitir —</option>
                {candidatosPend.map((c) => (
                  <option key={c.id} value={c.id}>
                    id {c.id} · cheques {fmtMoney(c.total_cheques)} · transf. {fmtMoney(c.total_txs)}
                    {c.fecha_ultima_acreditacion ? ` · acred. ${fmtFecha(c.fecha_ultima_acreditacion)}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Componente retenciones */}
          {retConc.estado === 'ya_cargadas' && (
            <p className="text-xs font-bold text-amber-400">
              Ya existe un recibo con este total de retenciones — posible duplicado:{' '}
              {(retConc.recibos || []).map((c) => `#${c.nro_recibo || c.id} (${c.estado})`).join(', ')}
            </p>
          )}
          {accion === 'esperar' && (
            <p className="text-[11px] text-gray-400">
              El documento queda en la cola hasta que los valores entren a YiQi (reintentá el análisis más adelante).
            </p>
          )}
        </div>

        {/* Cliente + acciones */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 pt-1">
          <div className="flex-1 min-w-0">
            {(cliente.candidatos || []).length > 0 ? (
              <select
                value={clienteSel || ''}
                onChange={(e) => setClienteSel(Number(e.target.value) || '')}
                disabled={bloqueado}
                className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
              >
                <option value="">— Elegir cliente en YiQi —</option>
                {(cliente.candidatos || []).map((c) => (
                  <option key={c.clie_id_clie} value={c.clie_id_clie}>
                    {c.razon_social} (ID {c.clie_id_clie} · CUIT {c.cuit})
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="flex items-center gap-1.5 text-xs text-amber-400 shrink-0" title={MOTIVO_CLIENTE[cliente.motivo] || ''}>
                    <UserX className="w-3.5 h-3.5" /> Sin cliente identificado
                  </span>
                  <CuitRetry onRetry={onReanalizar} disabled={bloqueado} />
                  <ManualSearch onSearch={onBuscarCliente} disabled={bloqueado} />
                </div>
                {MOTIVO_CLIENTE[cliente.motivo] && (
                  <p className="text-[11px] text-gray-500">{MOTIVO_CLIENTE[cliente.motivo]}</p>
                )}
                {Array.isArray(resultadosBusqueda) && resultadosBusqueda.length > 0 && (
                  <div className="rounded-lg border border-gray-700 divide-y divide-gray-700/40 overflow-hidden">
                    {resultadosBusqueda.map((c) => (
                      <button
                        key={c.clie_id_clie}
                        type="button"
                        disabled={bloqueado}
                        onClick={() => onReanalizar(c.cuit)}
                        className="w-full text-left px-3 py-1.5 text-[11px] text-gray-300 hover:bg-gray-700/40 disabled:opacity-50"
                        title="Re-analizar la OP con este cliente"
                      >
                        {c.razon_social} <span className="text-gray-500">(ID {c.clie_id_clie} · CUIT {c.cuit})</span>
                      </button>
                    ))}
                  </div>
                )}
                {Array.isArray(resultadosBusqueda) && resultadosBusqueda.length === 0 && (
                  <p className="text-[11px] text-gray-500">Sin resultados en el padrón de clientes de YiQi.</p>
                )}
              </div>
            )}
          </div>

          {/* Estado */}
          <div className="min-w-[150px]">
            {estado === 'ok' && resultado && (
              resultado.emitido ? (
                <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Recibo #{resultado.cobro_id} · Emitido
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-400 font-bold" title={resultado.emision_error}>
                  <AlertCircle className="w-3.5 h-3.5" /> Recibo #{resultado.cobro_id} · sin emitir
                </span>
              )
            )}
            {estado === 'conciliado' && (
              <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                <CheckCircle2 className="w-3.5 h-3.5" /> Conciliada
              </span>
            )}
            {estado === 'error' && resultado && (
              <span className="flex items-center gap-1 text-xs text-red-400 font-bold truncate" title={resultado.error}>
                <XCircle className="w-3.5 h-3.5" /> Error
              </span>
            )}
          </div>

          {/* Conciliar (todo ya aplicado) */}
          {(accion === 'conciliar' || accion === 'revisar') && estado !== 'ok' && (
            <button
              onClick={onConciliar}
              disabled={bloqueado}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black bg-gray-700 hover:bg-gray-600 text-gray-200 transition-all active:scale-95 whitespace-nowrap disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" /> Marcar conciliada
            </button>
          )}

          {/* Crear */}
          <button
            onClick={onConfirmar}
            disabled={!puedeCrear}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 whitespace-nowrap ${
              estado === 'ok' || estado === 'conciliado'
                ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                : estado === 'loading'
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : !puedeCrear
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : estado === 'error'
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
            }`}
          >
            {estado === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : estado === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" />
              : <ReceiptText className="w-3.5 h-3.5" />}
            {estado === 'ok' ? 'Creado'
              : estado === 'loading' ? 'Procesando…'
              : estado === 'error' ? 'Reintentar'
              : requiereEmitir ? 'Emitir pendiente + crear recibo'
              : 'Crear recibo de retenciones'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Formulario de carga manual de una Orden de Pago
// ---------------------------------------------------------------------------
// Se usa cuando el PDF no se pudo leer (escaneo malo, sin capa de texto) o
// cuando la extracción no sacó nada aprovechable. Arranca PRECARGADO con lo
// poco o mucho que el parser sí haya logrado leer: la idea es completar los
// huecos, no reescribir todo.
//
// Espeja `_a_float` del backend: acepta "1.234,56", "1234,56", "$ 1.234,56".
const parseImporte = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  let s = String(v).trim().replace(/\$/g, '').replace(/\s/g, '');
  if (!s) return null;
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

const sumaDe = (filas, campo) =>
  filas.reduce((acc, f) => acc + (parseImporte(f[campo]) || 0), 0);

// "1.000" es ambiguo: acá (y en el backend) el punto se lee como decimal, así
// que se interpreta como 1, no como mil. Quien carga a mano en formato
// argentino sin decimales cae seguro. Se marca el campo y se muestra cómo quedó
// interpretado para que el error se vea antes de mandarlo a YiQi.
const importeAmbiguo = (v) => /^\s*\d{1,3}(\.\d{3})+\s*$/.test(String(v ?? ''));

const FILA_FACTURA = { numero_documento: '', fecha: '', importe: '' };
const FILA_RETENCION = {
  tipo_retencion_id: '', tipo_texto: '', numero_certificado: '',
  base_imponible: '', alicuota: '', monto: '', fecha: '',
};
const FILA_MEDIO = { tipo: 'transferencia', banco: '', numero: '', fecha_vencimiento: '', importe: '' };

const TIPOS_MEDIO = [
  { v: 'transferencia', l: 'Transferencia' },
  { v: 'cheque', l: 'Cheque' },
  { v: 'cheque_diferido', l: 'Cheque diferido' },
  { v: 'otro', l: 'Otro' },
];

// Input compacto reutilizable dentro de las tablas del formulario.
const Celda = ({ value, onChange, placeholder, tipo = 'text', alerta, ancho }) => (
  <input
    type={tipo}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`${ancho || 'w-full'} bg-gray-900 border rounded-md px-2 py-1 text-xs text-white
      placeholder-gray-600 focus:outline-none focus:border-[#00a1df] ${
      alerta ? 'border-amber-500/60' : 'border-gray-600'}`}
  />
);

// Celda de importe: además de editar, muestra cómo quedó interpretado el valor
// cuando es ambiguo ("1.000" → $1,00), que es el error más fácil de cometer.
const CeldaImporte = ({ value, onChange, placeholder = '0,00', alerta }) => {
  const ambiguo = importeAmbiguo(value);
  return (
    <div>
      <Celda value={value} onChange={onChange} placeholder={placeholder}
        alerta={alerta || ambiguo} />
      {ambiguo && (
        <p className="text-[10px] text-amber-400 mt-0.5 leading-tight">
          se lee {fmtMoney(parseImporte(value))} — usá coma para decimales
        </p>
      )}
    </div>
  );
};

const BotonFila = ({ onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="text-[11px] text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700/50 transition-colors"
  >
    {children}
  </button>
);

const CargaManualOp = ({ op, tiposRetencion, onCargar, disabled, buscarCliente, traerFacturas }) => {
  const datos = op.orden_pago || {};
  const info = op.carga_manual || {};

  // Semilla: lo que el parser sí pudo leer. Los importes se pasan a string
  // porque los inputs son de texto (para poder tipear "1.234,56").
  const aTexto = (n) => (n === null || n === undefined ? '' : String(n));
  const [cab, setCab] = useState({
    cuit_agente: datos.cuit_agente || '',
    razon_social_agente: datos.razon_social_agente || '',
    numero_op: datos.numero_op || '',
    fecha_pago: datos.fecha_pago || '',
    total_pago: aTexto(datos.total_pago),
  });
  const [facturas, setFacturas] = useState(
    (datos.facturas || []).length
      ? datos.facturas.map((f) => ({
          numero_documento: f.numero_documento || '',
          fecha: f.fecha || '',
          importe: aTexto(f.importe),
        }))
      : [{ ...FILA_FACTURA }]
  );
  const [retenciones, setRetenciones] = useState(
    (datos.retenciones || []).length
      ? datos.retenciones.map((r) => ({
          tipo_retencion_id: r.tipo_sugerido_id || '',
          tipo_texto: r.tipo_texto || '',
          numero_certificado: r.numero_certificado || '',
          base_imponible: aTexto(r.base_imponible),
          alicuota: aTexto(r.alicuota),
          monto: aTexto(r.monto),
          fecha: r.fecha || '',
        }))
      : [{ ...FILA_RETENCION }]
  );
  const [medios, setMedios] = useState(
    (datos.medios_pago || []).length
      ? datos.medios_pago.map((m) => ({
          tipo: m.tipo || 'transferencia',
          banco: m.banco || '',
          numero: m.numero || '',
          fecha_vencimiento: m.fecha_vencimiento || '',
          importe: aTexto(m.importe),
        }))
      : [{ ...FILA_MEDIO }]
  );

  const editar = (setter) => (idx, campo, val) =>
    setter((filas) => filas.map((f, i) => (i === idx ? { ...f, [campo]: val } : f)));
  const agregar = (setter, plantilla) => () => setter((filas) => [...filas, { ...plantilla }]);
  const quitar = (setter) => (idx) =>
    setter((filas) => (filas.length > 1 ? filas.filter((_, i) => i !== idx) : filas));

  // --- Datos que se pueden traer sin tipear -------------------------------
  const [candidatos, setCandidatos] = useState([]);      // clientes del padrón
  const [buscando, setBuscando] = useState(false);
  const [facturasYiqi, setFacturasYiqi] = useState(null); // null = no consultado
  const [cargandoFact, setCargandoFact] = useState(false);
  const [cuitSugerido, setCuitSugerido] = useState(false);

  const aplicarCliente = useCallback((c) => {
    setCab((prev) => ({
      ...prev,
      cuit_agente: c.cuit || prev.cuit_agente,
      razon_social_agente: prev.razon_social_agente || c.razon_social || '',
    }));
    setCandidatos([]);
  }, []);

  // Al abrir el formulario sin CUIT, se intenta resolverlo con el nombre de
  // cliente que se tipeó en la carga ("Sanatorio Otamendi"). Si el padrón
  // devuelve uno solo, se precarga marcado como sugerido; si devuelve varios,
  // se listan para elegir. Nunca se pisa lo que el admin ya haya escrito.
  const nombreCarga = op.cliente_nombre || '';
  useEffect(() => {
    let vivo = true;
    if (datos.cuit_agente || !nombreCarga || nombreCarga.trim().length < 2) return undefined;
    (async () => {
      setBuscando(true);
      try {
        // El nombre de la carga es texto libre ("Sanatorio Otamendi S.A.") y
        // puede no coincidir literal con el padrón. Si la frase completa no da
        // nada, se reintenta con la palabra más larga —la más distintiva—
        // ignorando genéricos que matchearían medio padrón.
        let res = await buscarCliente(nombreCarga);
        if (!vivo) return;
        if (!res.length) {
          const genericas = ['sanatorio', 'clinica', 'clínica', 'hospital', 'centro',
            'instituto', 'fundacion', 'fundación', 'asociacion', 'asociación'];
          const palabra = nombreCarga
            .split(/[\s.,]+/)
            .filter((w) => w.length >= 4 && !genericas.includes(w.toLowerCase()))
            .sort((a, b) => b.length - a.length)[0];
          if (palabra && palabra.toLowerCase() !== nombreCarga.trim().toLowerCase()) {
            res = await buscarCliente(palabra);
            if (!vivo) return;
          }
        }
        if (res.length === 1) {
          aplicarCliente(res[0]);
          setCuitSugerido(true);
        } else if (res.length > 1) {
          setCandidatos(res);
        }
      } finally {
        if (vivo) setBuscando(false);
      }
    })();
    return () => { vivo = false; };
    // Sólo al montar: después manda lo que edite el admin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Con el CUIT completo se traen las facturas pendientes del cliente en YiQi.
  // Son las que el admin va a poder tildar en vez de tipear número e importe.
  const cuitLimpio = (cab.cuit_agente || '').replace(/\D/g, '');
  useEffect(() => {
    let vivo = true;
    if (cuitLimpio.length !== 11) { setFacturasYiqi(null); return undefined; }
    (async () => {
      setCargandoFact(true);
      try {
        const f = await traerFacturas(cuitLimpio);
        if (vivo) setFacturasYiqi(f);
      } finally {
        if (vivo) setCargandoFact(false);
      }
    })();
    return () => { vivo = false; };
  }, [cuitLimpio, traerFacturas]);

  // Tildar una factura de YiQi la vuelca a la tabla de facturas de la OP.
  const numerosCargados = facturas
    .map((f) => String(f.numero_documento || '').replace(/\D/g, '').replace(/^0+/, ''))
    .filter(Boolean);
  const estaCargada = (f) => {
    const n = String(f.numero || '').replace(/\D/g, '').replace(/^0+/, '');
    return Boolean(n) && numerosCargados.includes(n);
  };
  const alternarFactura = (f) => {
    const etiqueta = f.punto_venta ? `${f.punto_venta}-${f.numero}` : String(f.numero || '');
    if (estaCargada(f)) {
      setFacturas((filas) => {
        const n = String(f.numero || '').replace(/\D/g, '').replace(/^0+/, '');
        const quedan = filas.filter(
          (x) => String(x.numero_documento || '').replace(/\D/g, '').replace(/^0+/, '') !== n
        );
        return quedan.length ? quedan : [{ ...FILA_FACTURA }];
      });
      return;
    }
    const fila = {
      numero_documento: etiqueta,
      fecha: f.fecha || '',
      // El importe de la OP suele ser el total de la factura; el admin lo ajusta
      // si el pago es parcial.
      importe: f.total !== null && f.total !== undefined ? String(f.total) : '',
    };
    setFacturas((filas) => {
      const vacias = filas.filter((x) => !String(x.numero_documento || '').trim() && !x.importe);
      const utiles = filas.filter((x) => String(x.numero_documento || '').trim() || x.importe);
      return vacias.length && !utiles.length ? [fila] : [...utiles, fila];
    });
  };

  // --- Validación aritmética en vivo -------------------------------------
  // Misma identidad que valida el backend antes de escribir en YiQi:
  //   Σ facturas − Σ retenciones = total del pago = Σ medios de pago
  const sFact = sumaDe(facturas, 'importe');
  const sRet = sumaDe(retenciones, 'monto');
  const sMed = sumaDe(medios, 'importe');
  const total = parseImporte(cab.total_pago);
  const neto = sFact - sRet;
  const TOL = 0.05;
  const totalOk = total === null || Math.abs(neto - total) <= TOL;
  const mediosOk = sMed === 0 || total === null || Math.abs(sMed - total) <= TOL;
  const cuitOk = (cab.cuit_agente || '').replace(/\D/g, '').length === 11;
  const hayRet = retenciones.some((r) => parseImporte(r.monto));
  const cuadra = totalOk && mediosOk;
  const puedeCargar = !disabled && cuitOk && hayRet;

  const enviar = () => {
    const limpiar = (s) => String(s ?? '').trim();
    onCargar({
      cuit_agente: limpiar(cab.cuit_agente).replace(/\D/g, ''),
      razon_social_agente: limpiar(cab.razon_social_agente),
      numero_op: limpiar(cab.numero_op),
      fecha_pago: limpiar(cab.fecha_pago) || null,
      total_pago: parseImporte(cab.total_pago),
      facturas: facturas
        .filter((f) => limpiar(f.numero_documento) || parseImporte(f.importe))
        .map((f) => ({
          numero_documento: limpiar(f.numero_documento),
          fecha: limpiar(f.fecha) || null,
          importe: parseImporte(f.importe),
        })),
      retenciones: retenciones
        .filter((r) => parseImporte(r.monto))
        .map((r) => ({
          tipo_retencion_id: r.tipo_retencion_id ? Number(r.tipo_retencion_id) : null,
          tipo_texto: limpiar(r.tipo_texto),
          numero_certificado: limpiar(r.numero_certificado),
          base_imponible: parseImporte(r.base_imponible),
          alicuota: parseImporte(r.alicuota),
          monto: parseImporte(r.monto),
          fecha: limpiar(r.fecha) || null,
        })),
      medios_pago: medios
        .filter((m) => parseImporte(m.importe))
        .map((m) => ({
          tipo: m.tipo || 'otro',
          banco: limpiar(m.banco),
          numero: limpiar(m.numero),
          fecha_vencimiento: limpiar(m.fecha_vencimiento) || null,
          importe: parseImporte(m.importe),
        })),
    });
  };

  const th = 'py-1.5 px-2 text-left text-gray-500 uppercase text-[9px]';

  return (
    <div className="space-y-4">
      {/* Motivo */}
      <div className="bg-amber-900/15 border border-amber-500/40 rounded-lg p-3">
        <p className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5" /> Carga manual
        </p>
        <p className="text-[11px] text-amber-300/80 mt-1">
          {info.mensaje || 'Completá los datos de la orden de pago a mano.'}
        </p>
        {info.marcado_ilegible_en_subida && (
          <p className="text-[11px] text-amber-300/60 mt-1">
            Este documento ya venía marcado como ilegible desde la carga.
          </p>
        )}
        {(datos.facturas || []).length + (datos.retenciones || []).length > 0 && (
          <p className="text-[11px] text-emerald-400/80 mt-1">
            Se precargó lo que sí se pudo leer del PDF. Revisalo y completá lo que falte.
          </p>
        )}
      </div>

      {/* Cabecera */}
      <div>
        <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">Datos de la orden de pago</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="md:col-span-1">
            <Celda
              value={cab.cuit_agente}
              onChange={(v) => setCab((c) => ({ ...c, cuit_agente: v }))}
              placeholder="CUIT del cliente *"
              alerta={!cuitOk}
            />
          </div>
          <div className="md:col-span-2">
            <Celda
              value={cab.razon_social_agente}
              onChange={(v) => setCab((c) => ({ ...c, razon_social_agente: v }))}
              placeholder="Razón social"
            />
          </div>
          <Celda
            value={cab.numero_op}
            onChange={(v) => setCab((c) => ({ ...c, numero_op: v }))}
            placeholder="Nº de OP"
          />
          <Celda
            value={cab.fecha_pago}
            onChange={(v) => setCab((c) => ({ ...c, fecha_pago: v }))}
            placeholder="Fecha de pago"
            tipo="date"
          />
        </div>
        {!cuitOk && (
          <p className="text-[11px] text-amber-400 mt-1">
            El CUIT del cliente es obligatorio: sin él no se pueden traer las facturas pendientes ni los recibos.
          </p>
        )}
        {buscando && (
          <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Buscando “{nombreCarga}” en el padrón de YiQi…
          </p>
        )}
        {cuitSugerido && cuitOk && (
          <p className="text-[11px] text-emerald-400/90 mt-1">
            CUIT sugerido a partir del cliente de la carga (“{nombreCarga}”). Verificalo contra el PDF.
          </p>
        )}
        {candidatos.length > 0 && (
          <div className="mt-2">
            <p className="text-[11px] text-gray-400 mb-1">
              “{nombreCarga}” coincide con varios clientes. Elegí cuál es:
            </p>
            <div className="rounded-lg border border-gray-700 divide-y divide-gray-700/40 overflow-hidden">
              {candidatos.map((c) => (
                <button
                  key={c.clie_id_clie}
                  type="button"
                  onClick={() => { aplicarCliente(c); setCuitSugerido(true); }}
                  className="w-full text-left px-3 py-1.5 text-[11px] text-gray-300 hover:bg-gray-700/40"
                >
                  {c.razon_social} <span className="text-gray-500">(ID {c.clie_id_clie} · CUIT {c.cuit})</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Facturas pendientes en YiQi: se tildan en vez de tipearlas */}
      {cuitOk && (
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">
            Facturas pendientes del cliente en YiQi
          </p>
          {cargandoFact ? (
            <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Buscando facturas pendientes…
            </p>
          ) : (facturasYiqi || []).length === 0 ? (
            <p className="text-[11px] text-gray-500">
              {facturasYiqi === null
                ? 'Completá el CUIT para traerlas.'
                : 'El cliente no tiene facturas pendientes en YiQi. Cargalas a mano abajo.'}
            </p>
          ) : (
            <>
              <p className="text-[11px] text-gray-500 mb-1.5">
                Tildá las que paga esta orden de pago: se agregan abajo con su número e importe.
              </p>
              <div className="rounded-lg border border-gray-700 divide-y divide-gray-700/40 overflow-hidden max-h-56 overflow-y-auto">
                {(facturasYiqi || []).map((f) => {
                  const sel = estaCargada(f);
                  return (
                    <button
                      key={f.fact_id}
                      type="button"
                      onClick={() => alternarFactura(f)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-[11px] text-left transition-colors ${
                        sel ? 'bg-[#00a1df]/10 text-white' : 'text-gray-300 hover:bg-gray-700/40'}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-3.5 h-3.5 rounded border shrink-0 flex items-center justify-center ${
                          sel ? 'bg-[#00a1df] border-[#00a1df]' : 'border-gray-600'}`}>
                          {sel && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <span className="font-mono truncate">
                          {f.punto_venta ? `${f.punto_venta}-` : ''}{f.numero}
                        </span>
                        <span className="text-gray-500 truncate">{fmtFecha(f.fecha)}</span>
                      </span>
                      <span className="shrink-0 text-gray-400">
                        total {fmtMoney(f.total)} · pendiente {fmtMoney(f.pendiente)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Facturas */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Facturas que paga la OP</p>
          <BotonFila onClick={agregar(setFacturas, FILA_FACTURA)} title="Agregar factura">+ factura</BotonFila>
        </div>
        <div className="rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-900/40">
                <th className={th}>Nº de comprobante</th>
                <th className={th}>Fecha</th>
                <th className={`${th} text-right`}>Importe</th>
                <th className={`${th} w-10`} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {facturas.map((f, i) => (
                <tr key={i}>
                  <td className="py-1 px-2">
                    <Celda value={f.numero_documento}
                      onChange={(v) => editar(setFacturas)(i, 'numero_documento', v)}
                      placeholder="0001-00001234" />
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={f.fecha} tipo="date"
                      onChange={(v) => editar(setFacturas)(i, 'fecha', v)} />
                  </td>
                  <td className="py-1 px-2">
                    <CeldaImporte value={f.importe}
                      onChange={(v) => editar(setFacturas)(i, 'importe', v)} />
                  </td>
                  <td className="py-1 px-2 text-center">
                    <BotonFila onClick={() => quitar(setFacturas)(i)} title="Quitar">×</BotonFila>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retenciones */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
            Retenciones — una por certificado <span className="text-amber-400">(obligatorio)</span>
          </p>
          <BotonFila onClick={agregar(setRetenciones, FILA_RETENCION)} title="Agregar retención">+ retención</BotonFila>
        </div>
        <div className="rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-900/40">
                <th className={`${th} w-44`}>Tipo</th>
                <th className={th}>Nº certificado</th>
                <th className={`${th} text-right`}>Base</th>
                <th className={`${th} text-right w-20`}>Alíc. %</th>
                <th className={`${th} text-right`}>Monto</th>
                <th className={th}>Fecha</th>
                <th className={`${th} w-10`} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {retenciones.map((r, i) => (
                <tr key={i}>
                  <td className="py-1 px-2">
                    <select
                      value={r.tipo_retencion_id || ''}
                      onChange={(e) => editar(setRetenciones)(i, 'tipo_retencion_id', e.target.value)}
                      className={`w-full bg-gray-900 border rounded-md px-2 py-1 text-xs text-white
                        focus:outline-none focus:border-[#00a1df] ${
                        r.tipo_retencion_id ? 'border-gray-600' : 'border-amber-500/60'}`}
                    >
                      <option value="">— Tipo —</option>
                      {tiposRetencion.map((t) => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={r.numero_certificado}
                      onChange={(v) => editar(setRetenciones)(i, 'numero_certificado', v)}
                      placeholder="Nº" />
                  </td>
                  <td className="py-1 px-2">
                    <CeldaImporte value={r.base_imponible}
                      onChange={(v) => editar(setRetenciones)(i, 'base_imponible', v)} />
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={r.alicuota}
                      onChange={(v) => editar(setRetenciones)(i, 'alicuota', v)}
                      placeholder="0,00" />
                  </td>
                  <td className="py-1 px-2">
                    <CeldaImporte value={r.monto}
                      onChange={(v) => editar(setRetenciones)(i, 'monto', v)}
                      alerta={!parseImporte(r.monto)} />
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={r.fecha} tipo="date"
                      onChange={(v) => editar(setRetenciones)(i, 'fecha', v)} />
                  </td>
                  <td className="py-1 px-2 text-center">
                    <BotonFila onClick={() => quitar(setRetenciones)(i)} title="Quitar">×</BotonFila>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!hayRet && (
          <p className="text-[11px] text-amber-400 mt-1">
            Cargá al menos una retención con monto: es lo que se va a crear como recibo en YiQi.
          </p>
        )}
      </div>

      {/* Medios de pago */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
            Medios de pago <span className="text-gray-600">(sirven para conciliar contra los recibos existentes)</span>
          </p>
          <BotonFila onClick={agregar(setMedios, FILA_MEDIO)} title="Agregar medio">+ medio</BotonFila>
        </div>
        <div className="rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-900/40">
                <th className={`${th} w-36`}>Tipo</th>
                <th className={th}>Banco</th>
                <th className={th}>Número</th>
                <th className={th}>Vencimiento</th>
                <th className={`${th} text-right`}>Importe</th>
                <th className={`${th} w-10`} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {medios.map((m, i) => (
                <tr key={i}>
                  <td className="py-1 px-2">
                    <select
                      value={m.tipo}
                      onChange={(e) => editar(setMedios)(i, 'tipo', e.target.value)}
                      className="w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00a1df]"
                    >
                      {TIPOS_MEDIO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={m.banco} onChange={(v) => editar(setMedios)(i, 'banco', v)} placeholder="Banco" />
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={m.numero} onChange={(v) => editar(setMedios)(i, 'numero', v)} placeholder="Nº" />
                  </td>
                  <td className="py-1 px-2">
                    <Celda value={m.fecha_vencimiento} tipo="date"
                      onChange={(v) => editar(setMedios)(i, 'fecha_vencimiento', v)} />
                  </td>
                  <td className="py-1 px-2">
                    <CeldaImporte value={m.importe}
                      onChange={(v) => editar(setMedios)(i, 'importe', v)} />
                  </td>
                  <td className="py-1 px-2 text-center">
                    <BotonFila onClick={() => quitar(setMedios)(i)} title="Quitar">×</BotonFila>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Control aritmético en vivo */}
      <div className={`rounded-lg border p-3 space-y-2 ${
        cuadra ? 'bg-gray-900/40 border-gray-700' : 'bg-red-900/15 border-red-500/40'}`}>
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Control aritmético</p>
          <span className={`text-[11px] font-bold ${cuadra ? 'text-green-400' : 'text-red-300'}`}>
            {cuadra ? 'Los números cierran' : 'Los números no cierran'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Campo label="Σ Facturas" value={fmtMoney(sFact)} strong />
          <Campo label="Σ Retenciones" value={fmtMoney(sRet)} strong />
          <Campo label="Σ Medios de pago" value={fmtMoney(sMed)} />
          <div>
            <p className="text-[10px] text-gray-500 uppercase">Total del pago</p>
            <div className="flex items-center gap-1 mt-0.5">
              <CeldaImporte value={cab.total_pago}
                onChange={(v) => setCab((c) => ({ ...c, total_pago: v }))}
                alerta={!totalOk} />
              {Math.abs(neto) > TOL && (
                <BotonFila
                  onClick={() => setCab((c) => ({ ...c, total_pago: neto.toFixed(2) }))}
                  title="Usar facturas − retenciones">=</BotonFila>
              )}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">
          Facturas − retenciones = <span className={totalOk ? 'text-gray-300' : 'text-red-300 font-bold'}>{fmtMoney(neto)}</span>
          {total !== null && <> · total declarado {fmtMoney(total)}</>}
        </p>
        {!totalOk && (
          <p className="text-[11px] text-red-300">
            La identidad Σfacturas − Σretenciones = total no cierra. Revisá los importes antes de continuar.
          </p>
        )}
        {!mediosOk && (
          <p className="text-[11px] text-red-300">
            La suma de medios de pago ({fmtMoney(sMed)}) no coincide con el total del pago.
          </p>
        )}
        {!cuadra && (
          <p className="text-[11px] text-gray-500">
            Podés continuar igual: la tarjeta va a mostrar la advertencia y vas a poder revisarla antes de crear el recibo.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={enviar}
          disabled={!puedeCargar}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-[#00a1df] hover:bg-[#0090c8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {disabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Cargar y buscar en YiQi
        </button>
      </div>
      <p className="text-[11px] text-gray-500 text-right">
        No se crea nada todavía: con estos datos se busca el cliente, sus facturas pendientes y los recibos ya aplicados.
      </p>
    </div>
  );
};

// Reintento de análisis de OP con CUIT indicado a mano (cuando la extracción
// y el backstop del texto del PDF no pudieron resolver el cliente).
const CuitRetry = ({ onRetry, disabled }) => {
  const [cuit, setCuit] = useState('');
  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={cuit}
        onChange={(e) => setCuit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onRetry(cuit); }}
        placeholder="CUIT del cliente…"
        disabled={disabled}
        className="w-40 bg-gray-900 border border-amber-500/50 rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-600 focus:border-[#00a1df] focus:outline-none disabled:opacity-50 font-mono"
      />
      <button
        onClick={() => onRetry(cuit)}
        disabled={disabled}
        className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs font-bold disabled:opacity-50 whitespace-nowrap"
        title="Volver a analizar la OP usando este CUIT"
      >
        Reintentar con CUIT
      </button>
    </div>
  );
};

const ManualSearch = ({ onSearch, disabled }) => {
  const [q, setQ] = useState('');
  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSearch(q); }}
        placeholder="Buscar cliente…"
        disabled={disabled}
        className="w-32 bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-600 focus:border-[#00a1df] focus:outline-none disabled:opacity-50"
      />
      <button
        onClick={() => onSearch(q)}
        disabled={disabled}
        className="p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
        title="Buscar"
      >
        <Search className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default RetencionesClientesPage;
